
import Vue from 'vue'
import { apiService } from '../../service/api.service'

/** Wire names → what an admin actually needs to read. */
const KIND_LABELS = {
  recipe: 'Model recipes',
  recipe_index: 'Recipe index',
  parser_list: 'Parser names (closed set)',
  parser_map: 'Model → parser table',
  engine_args: 'Supported arguments',
  model_config: 'Model config files',
  arch_registry: 'Engine architecture registry',
  index: 'Index',
  other: 'Other'
}

export default Vue.extend({
  name: 'OfflineMirrorDialog',
  props: {
    showOfflineMirrorDialog: { type: Boolean, default: false }
  },
  data () {
    return {
      loading: false,
      planLoading: false,
      rechecking: false,
      syncing: false,
      refresh: false,
      tab: 0,
      status: null,
      plan: null,
      records: [],
      recordKinds: [],
      recordKind: null,
      recordSearch: '',
      recordTotal: 0,
      recordOffset: 0,
      recordLimit: 25,
      recordsLoading: false,
      showRecord: false,
      record: null,
      hubError: '',
      // The sync runs in the hub, not in this request, so progress is polled.
      poller: null,
      flushingPlanner: false,
      plannerFlush: null
    }
  },
  computed: {
    /** Three states, not two: forced offline, forced online, or measured. */
    modeChip () {
      const net = (this.status && this.status.connectivity) || null
      if (!net) return null
      if (net.mode === 'forced-offline') {
        return { color: 'orange lighten-4', icon: 'mdi-lan-disconnect', text: 'Offline (forced)' }
      }
      if (net.mode === 'forced-online') {
        return { color: 'blue lighten-5', icon: 'mdi-web', text: 'Online (forced)' }
      }
      if (net.internet === true) {
        return { color: 'green lighten-4', icon: 'mdi-web', text: 'Internet reachable' }
      }
      if (net.internet === false) {
        return { color: 'orange lighten-4', icon: 'mdi-lan-disconnect', text: 'No internet' }
      }
      return { color: 'grey lighten-2', icon: 'mdi-help-circle-outline', text: 'Not measured yet' }
    },
    connectivity () {
      const net = (this.status && this.status.connectivity) || {}
      const age = net.age === null || net.age === undefined
        ? '' : `measured ${this.agoText(net.age)}`
      const via = net.source === 'traffic'
        ? 'from a real request' : (net.source === 'probe' ? 'by probe' : '')
      const detail = [age, via].filter(Boolean).join(', ') ||
        (net.targets ? `will probe: ${net.targets.join(', ')}` : '')

      if (net.mode === 'forced-offline') {
        return {
          icon: 'mdi-lock-outline',
          iconColor: 'orange darken-2',
          canRecheck: false,
          text: 'Offline is forced by configuration (OFFLINE_MODE=1). The plan path never ' +
            'touches the internet; only this sync does.',
          detail: 'Set OFFLINE_MODE=auto to let the hub decide for itself.'
        }
      }
      if (net.mode === 'forced-online') {
        return {
          icon: 'mdi-lock-open-variant-outline',
          iconColor: 'blue darken-2',
          canRecheck: false,
          text: 'Online is forced by configuration (OFFLINE_MODE=0). Mirror misses go to the ' +
            'internet even when there is none, which costs a full timeout each.',
          detail: 'Set OFFLINE_MODE=auto to let the hub decide for itself.'
        }
      }
      if (net.internet === true) {
        return {
          icon: 'mdi-web',
          iconColor: 'green darken-2',
          canRecheck: true,
          text: 'The hub can reach the internet, so it fetches what the mirror is missing.',
          detail
        }
      }
      if (net.internet === false) {
        return {
          icon: 'mdi-lan-disconnect',
          iconColor: 'orange darken-2',
          canRecheck: true,
          text: 'The hub cannot reach the internet, so it is answering from the mirror alone ' +
            'and not waiting on timeouts.',
          detail
        }
      }
      return {
        icon: 'mdi-help-circle-outline',
        iconColor: 'grey darken-1',
        canRecheck: true,
        text: 'Connectivity has not been measured yet — the hub measures it on the first ' +
          'request that needs it.',
        detail
      }
    },
    totalRecords () {
      const stats = (this.status && this.status.stats) || {}
      return Object.keys(stats).reduce((sum, k) => sum + (stats[k].total || 0), 0)
    },
    contentRows () {
      const stats = (this.status && this.status.stats) || {}
      return Object.keys(stats)
        .sort((a, b) => (stats[b].total || 0) - (stats[a].total || 0))
        .map((kind) => ({
          kind,
          label: KIND_LABELS[kind] || kind,
          positive: stats[kind].positive || 0,
          negative: stats[kind].negative || 0
        }))
    },
    missingSummary () {
      const missing = (this.plan && this.plan.missing) || {}
      const out = {}
      Object.keys(missing).forEach((k) => { if (missing[k]) out[k] = missing[k] })
      return out
    },
    progress () {
      return (this.status && this.status.progress) || null
    },
    lastResult () {
      const r = this.status && this.status.last_result
      if (!r || r.error) return null
      return r
    },
    resultType () {
      const r = this.lastResult
      if (!r) return 'info'
      if (r.failed) return 'warning'
      return 'success'
    },
    kindOptions () {
      return this.recordKinds.map((kind) => ({ text: this.kindLabel(kind), value: kind }))
    },
    lastSyncText () {
      const ts = this.status && this.status.last_sync
      if (!ts) return ''
      return new Date(ts * 1000).toLocaleString()
    }
  },
  watch: {
    showOfflineMirrorDialog (open) {
      if (open) this.refreshAll()
      else this.stopPolling()
    },
    tab (value) {
      // Kayitlar ilk kez o sekmeye gecildiginde yukleniyor: 482 satiri dialog acilirken
      // cekmek, cogu ziyaretin hic bakmadigi bir veri icin bekleme uretirdi.
      if (value === 1 && !this.records.length) this.loadRecords()
    }
  },
  beforeDestroy () {
    this.stopPolling()
  },
  methods: {
    kindLabel (kind) {
      return KIND_LABELS[kind] || kind
    },
    closeDialog () {
      this.stopPolling()
      this.$emit('close-offline-mirror')
    },
    async refreshAll () {
      await this.fetchStatus()
      // The difference is only meaningful once we know what is in hand.
      if (!this.hubError) await this.fetchPlan()
    },
    async fetchStatus () {
      this.loading = true
      try {
        const response = await apiService.get('admin/mirror/status')
        this.status = response.data
        this.hubError = ''
        // A sync started from another tab (or an earlier visit) is still ours to show.
        if (this.status.syncing) {
          this.syncing = true
          this.startPolling()
        }
      } catch (error) {
        const message = (error.response && error.response.data && error.response.data.message)
        this.hubError = message || 'Could not reach application-hub-service.'
        this.status = null
      } finally {
        this.loading = false
      }
    },
    async recheckConnectivity () {
      this.rechecking = true
      try {
        const response = await apiService.post('admin/mirror/connectivity', {})
        // Tek alani yaz, butun status'u degistirme: senkron ilerlemesi ayni objede duruyor.
        if (this.status) this.status = { ...this.status, connectivity: response.data }
        // Yeniden erisim kazandiysak fark hesabi da degisti (indeksler tazelenebilir).
        if (response.data && response.data.internet) this.fetchPlan()
      } catch (error) {
        const message = (error.response && error.response.data && error.response.data.message)
        this.hubError = message || 'Could not check connectivity.'
      } finally {
        this.rechecking = false
      }
    },
    async fetchPlan () {
      this.planLoading = true
      try {
        const response = await apiService.get('admin/mirror/plan')
        this.plan = response.data
      } catch (error) {
        // A failing difference is not a failing mirror: what is in hand still works.
        this.plan = null
      } finally {
        this.planLoading = false
      }
    },
    async flushPlannerCache () {
      this.flushingPlanner = true
      try {
        const response = await apiService.post('admin/planner-cache/flush', {})
        this.plannerFlush = response.data
      } catch (error) {
        const message = (error.response && error.response.data && error.response.data.message)
        this.hubError = message || 'Could not clear the planner cache.'
      } finally {
        this.flushingPlanner = false
      }
    },
    async fetchMissing () {
      this.syncing = true
      try {
        await apiService.post('admin/mirror/fetch-missing',
          this.refresh ? { refresh: true } : {})
        this.startPolling()
      } catch (error) {
        this.syncing = false
        const message = (error.response && error.response.data && error.response.data.message)
        this.hubError = message || 'Could not start the sync.'
      }
    },
    startPolling () {
      this.stopPolling()
      // 3s: the first run is hundreds of URLs and takes minutes, so this is about keeping
      // the bar moving, not about latency.
      this.poller = setInterval(async () => {
        try {
          const response = await apiService.get('admin/mirror/status')
          this.status = response.data
          if (!this.status.syncing) {
            this.syncing = false
            this.stopPolling()
            // The difference has changed by definition; recompute it.
            this.fetchPlan()
          }
        } catch (error) {
          this.syncing = false
          this.stopPolling()
        }
      }, 3000)
    },
    prettySize (bytes) {
      if (!bytes) return '—'
      if (bytes < 1024) return bytes + ' B'
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
      return (bytes / 1024 / 1024).toFixed(1) + ' MB'
    },
    agoText (seconds) {
      if (seconds < 60) return `${Math.round(seconds)}s ago`
      if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`
      return `${Math.round(seconds / 3600)}h ago`
    },
    shortDate (ts) {
      if (!ts) return '—'
      return new Date(ts * 1000).toLocaleDateString()
    },
    resetAndLoadRecords () {
      this.recordOffset = 0
      this.loadRecords()
    },
    pageRecords (direction) {
      this.recordOffset = Math.max(0, this.recordOffset + direction * this.recordLimit)
      this.loadRecords()
    },
    async loadRecords () {
      this.recordsLoading = true
      try {
        const response = await apiService.get('admin/mirror/records', {
          params: {
            kind: this.recordKind || undefined,
            search: this.recordSearch || undefined,
            limit: this.recordLimit,
            offset: this.recordOffset
          }
        })
        this.records = response.data.rows || []
        this.recordTotal = response.data.total || 0
        if (response.data.kinds) this.recordKinds = response.data.kinds
      } catch (error) {
        this.records = []
        this.recordTotal = 0
      } finally {
        this.recordsLoading = false
      }
    },
    async openRecord (row) {
      this.record = null
      this.showRecord = true
      try {
        const response = await apiService.get('admin/mirror/record',
          { params: { url: row.url } })
        this.record = response.data
      } catch (error) {
        this.showRecord = false
      }
    },
    stopPolling () {
      if (this.poller) {
        clearInterval(this.poller)
        this.poller = null
      }
    }
  }
})
